import { IAppContext } from 'App/AppContext';
import DocumentOutputLocation, { DocumentOutputPurpose } from 'models/documentOutputLocation';
import ResourceList from 'models/resourceList';
import { apiSetOutputLocation, apiSetOutputLocationFolder } from 'services/Api/documentOutputLocationService';
import { apiAddNewList } from 'services/Api/listService';
import { apiRequest } from 'services/Auth/authConfig';

/** Other screens read the bindings from the locations summary, which reloads with the categories after every change. */
export async function refreshOutputLocationCaches(appContext: IAppContext): Promise<void> {
  await appContext.globalDataCache.lists.getItems(true);
}

/** The PUT of the binding; no listId unbinds. The caches are reloaded afterwards. */
export async function saveOutputLocationBinding(
  appContext: IAppContext,
  purpose: DocumentOutputPurpose,
  listId: number | undefined,
  templateLinkId: number | undefined,
): Promise<void> {
  const accessToken = await appContext.getAccessToken(apiRequest.scopes);
  await apiSetOutputLocation(purpose, listId, templateLinkId, accessToken);
  await refreshOutputLocationCaches(appContext);
}

/** The PUT of the start folder inside the bound category; no folderDriveItemId is the category root. */
export async function saveOutputLocationFolder(
  appContext: IAppContext,
  purpose: DocumentOutputPurpose,
  folderDriveItemId: string | undefined,
  folderName: string | undefined,
): Promise<void> {
  const accessToken = await appContext.getAccessToken(apiRequest.scopes);
  await apiSetOutputLocationFolder(purpose, folderDriveItemId, folderName, accessToken);
  await refreshOutputLocationCaches(appContext);
}

/**
 * Binds the purpose of the location to a category. The general reports keep their template only when the category
 * stays the same: a template belongs to its category.
 */
export async function bindCategory(
  appContext: IAppContext,
  location: DocumentOutputLocation,
  list: ResourceList,
): Promise<void> {
  const keepTemplate = location.purpose === DocumentOutputPurpose.GeneralReports && list.listId === location.listId;
  await saveOutputLocationBinding(
    appContext,
    location.purpose,
    list.listId,
    keepTemplate ? location.templateLinkId : undefined,
  );
}

/** Saves a new library category and adds it to the categories cache: the Library and the category menu. */
export async function createCategory(appContext: IAppContext, list: ResourceList): Promise<ResourceList> {
  const accessToken = await appContext.getAccessToken(apiRequest.scopes);
  const newList = await apiAddNewList(accessToken, list);
  appContext.globalDataCache.lists.add(newList);

  return newList;
}
